import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';

/*
  Soovana Wireless Knee Massager — live 3D product viewer.
  A single WebGL object the customer can freely rotate from any angle.
  Front : glossy white ergonomic dome + black touch panel + blue status LEDs
  Back  : concave black padded cavity + 14-LED red light-therapy ring
  Sides : two wide wrap straps with plastic buckles on one side
*/

const CDN_OK = typeof THREE !== 'undefined';

/* ergonomic "pebble" front outline — narrow rounded top, wider lower body */
function bodyShape() {
  const s = new THREE.Shape();
  s.moveTo(0, -1.5);
  s.bezierCurveTo(0.72, -1.5, 1.04, -0.85, 1.04, -0.15);
  s.bezierCurveTo(1.04, 0.5, 0.92, 1.16, 0.52, 1.42);
  s.bezierCurveTo(0.32, 1.55, 0.16, 1.57, 0, 1.57);
  s.bezierCurveTo(-0.16, 1.57, -0.32, 1.55, -0.52, 1.42);
  s.bezierCurveTo(-0.92, 1.16, -1.04, 0.5, -1.04, -0.15);
  s.bezierCurveTo(-1.04, -0.85, -0.72, -1.5, 0, -1.5);
  return s;
}

function roundedRectShape(w, h, r) {
  const s = new THREE.Shape();
  const x = -w / 2;
  const y = -h / 2;
  s.moveTo(x + r, y);
  s.lineTo(x + w - r, y);
  s.quadraticCurveTo(x + w, y, x + w, y + r);
  s.lineTo(x + w, y + h - r);
  s.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  s.lineTo(x + r, y + h);
  s.quadraticCurveTo(x, y + h, x, y + h - r);
  s.lineTo(x, y + r);
  s.quadraticCurveTo(x, y, x + r, y);
  return s;
}

/* flat ribbon band following a horizontal wrap curve; width spans vertically */
function ribbonGeometry(curve, segments, halfWidth) {
  const pts = curve.getPoints(segments);
  const positions = [];
  const uvs = [];
  const indices = [];
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i];
    positions.push(p.x, p.y + halfWidth, p.z);
    positions.push(p.x, p.y - halfWidth, p.z);
    const u = i / (pts.length - 1);
    uvs.push(u, 1, u, 0);
  }
  for (let i = 0; i < pts.length - 1; i++) {
    const a = i * 2;
    indices.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  g.setIndex(indices);
  g.computeVertexNormals();
  return g;
}

function makePanelTexture() {
  const c = document.createElement('canvas');
  c.width = 512;
  c.height = 440;
  const g = c.getContext('2d');
  g.fillStyle = '#08080a';
  g.fillRect(0, 0, c.width, c.height);
  g.strokeStyle = '#f2f2f2';
  g.fillStyle = '#f2f2f2';
  g.lineWidth = 5;
  g.lineCap = 'round';
  g.textAlign = 'center';
  g.textBaseline = 'middle';

  const waves = (cx, cy) => {
    g.beginPath();
    for (let k = 0; k < 3; k++) {
      const x = cx - 18 + k * 18;
      g.moveTo(x, cy + 15);
      g.quadraticCurveTo(x + 5, cy, x, cy - 15);
    }
    g.stroke();
  };
  waves(96, 66);
  g.beginPath();
  g.arc(256, 66, 19, Math.PI * -0.6, Math.PI * 1.6);
  g.stroke();
  g.beginPath();
  g.moveTo(256, 44);
  g.lineTo(256, 68);
  g.stroke();
  waves(416, 66);

  g.font = '600 24px Arial';
  g.fillText('HEATING', 256, 138);
  g.font = '500 20px Arial';
  ['LOW', 'MED', 'HIGH'].forEach((t, i) => {
    const x = 104 + i * 152;
    g.beginPath();
    g.moveTo(x - 38, 178);
    g.lineTo(x + 38, 178);
    g.stroke();
    g.fillText(t, x, 200);
  });

  const sun = (cx, cy) => {
    g.beginPath();
    g.arc(cx, cy, 11, 0, Math.PI * 2);
    g.stroke();
    for (let a = 0; a < 8; a++) {
      const an = (a / 8) * Math.PI * 2;
      g.beginPath();
      g.moveTo(cx + Math.cos(an) * 16, cy + Math.sin(an) * 16);
      g.lineTo(cx + Math.cos(an) * 22, cy + Math.sin(an) * 22);
      g.stroke();
    }
  };
  sun(156, 262);
  g.font = '500 18px Arial';
  g.fillText('INFRARED', 156, 300);
  sun(356, 262);
  g.fillText('LASER', 356, 300);

  g.font = '600 24px Arial';
  g.fillText('VIBRATION', 256, 350);
  g.font = '500 20px Arial';
  ['AUTO', 'LOW', 'HIGH'].forEach((t, i) => {
    const x = 104 + i * 152;
    g.beginPath();
    g.moveTo(x - 38, 390);
    g.lineTo(x + 38, 390);
    g.stroke();
    g.fillText(t, x, 412);
  });

  const tex = new THREE.CanvasTexture(c);
  tex.anisotropy = 8;
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

function buildDevice() {
  const group = new THREE.Group();

  // --- glossy white ergonomic shell ---
  const shellGeo = new THREE.ExtrudeGeometry(bodyShape(), {
    depth: 0.3,
    bevelEnabled: true,
    bevelThickness: 0.62,
    bevelSize: 0.5,
    bevelSegments: 22,
    curveSegments: 64,
  });
  shellGeo.center();
  const shellMat = new THREE.MeshPhysicalMaterial({
    color: 0xf7f8f9,
    roughness: 0.28,
    metalness: 0.0,
    clearcoat: 1.0,
    clearcoatRoughness: 0.1,
    envMapIntensity: 1.15,
  });
  const shell = new THREE.Mesh(shellGeo, shellMat);
  shell.castShadow = true;
  shell.receiveShadow = true;
  group.add(shell);

  const frontZ = 0.66;
  const backZ = -0.66;

  // --- black touch panel near the top ---
  const panelGroup = new THREE.Group();
  const bezelGeo = new THREE.ExtrudeGeometry(roundedRectShape(1.28, 1.12, 0.2), {
    depth: 0.05,
    bevelEnabled: true,
    bevelThickness: 0.04,
    bevelSize: 0.04,
    bevelSegments: 4,
    curveSegments: 24,
  });
  bezelGeo.center();
  const bezelMat = new THREE.MeshPhysicalMaterial({
    color: 0x0b0b0d,
    roughness: 0.22,
    metalness: 0.1,
    clearcoat: 1.0,
    clearcoatRoughness: 0.08,
    envMapIntensity: 1.2,
  });
  const bezel = new THREE.Mesh(bezelGeo, bezelMat);
  panelGroup.add(bezel);

  const panelTex = makePanelTexture();
  const panelMat = new THREE.MeshStandardMaterial({
    map: panelTex,
    emissive: 0xffffff,
    emissiveMap: panelTex,
    emissiveIntensity: 0.9,
    roughness: 0.3,
    metalness: 0.0,
  });
  const panel = new THREE.Mesh(new THREE.PlaneGeometry(1.12, 0.98), panelMat);
  panel.position.z = 0.05;
  panelGroup.add(panel);

  panelGroup.position.set(0, 0.66, frontZ + 0.06);
  panelGroup.rotation.x = -0.14;
  group.add(panelGroup);

  // --- blue status LEDs ---
  const ledMat = new THREE.MeshStandardMaterial({
    color: 0x2b6bff,
    emissive: 0x3f7bff,
    emissiveIntensity: 2.2,
    roughness: 0.25,
  });
  [0.0, -0.16, -0.33, -0.52].forEach((y, i) => {
    const led = new THREE.Mesh(new THREE.CapsuleGeometry(0.028, 0.08 + i * 0.02, 4, 8), ledMat);
    led.position.set(0, y, frontZ + 0.02);
    group.add(led);
  });

  // --- Type-C port + screws at the bottom ---
  const portMat = new THREE.MeshStandardMaterial({ color: 0x101013, roughness: 0.55 });
  const port = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.05, 0.05), portMat);
  port.position.set(0, -1.28, frontZ - 0.16);
  group.add(port);
  const screwMat = new THREE.MeshStandardMaterial({ color: 0xc2c6ca, roughness: 0.3, metalness: 0.9 });
  [-0.3, 0.3].forEach((x) => {
    const sc = new THREE.Mesh(new THREE.CylinderGeometry(0.032, 0.032, 0.03, 12), screwMat);
    sc.rotation.x = Math.PI / 2;
    sc.position.set(x, -1.28, frontZ - 0.16);
    group.add(sc);
  });

  // --- back concave cavity (black padding) ---
  const padMat = new THREE.MeshStandardMaterial({ color: 0x0c0c0e, roughness: 0.96, metalness: 0.0 });
  const cavity = new THREE.Mesh(
    new THREE.SphereGeometry(1.02, 48, 40, 0, Math.PI * 2, 0, Math.PI * 0.5),
    padMat
  );
  cavity.scale.set(1.0, 0.78, 1.0);
  cavity.rotation.x = -Math.PI / 2;
  cavity.position.set(0, 0, backZ + 0.5);
  group.add(cavity);

  const cushionGeo = new THREE.CapsuleGeometry(0.26, 0.6, 6, 12);
  [0.72, -0.72].forEach((y) => {
    const cu = new THREE.Mesh(cushionGeo, padMat);
    cu.rotation.z = Math.PI / 2;
    cu.position.set(0, y, backZ + 0.22);
    group.add(cu);
  });

  // --- red LED therapy ring (14 emitters) ---
  const ringGroup = new THREE.Group();
  const redMat = new THREE.MeshStandardMaterial({
    color: 0xff2020,
    emissive: 0xff1010,
    emissiveIntensity: 3.2,
    roughness: 0.35,
  });
  const ringR = 0.48;
  for (let i = 0; i < 14; i++) {
    const a = (i / 14) * Math.PI * 2;
    const dot = new THREE.Mesh(new THREE.SphereGeometry(0.05, 16, 16), redMat);
    dot.position.set(Math.cos(a) * ringR, Math.sin(a) * ringR, 0);
    ringGroup.add(dot);
  }
  ringGroup.add(
    new THREE.Mesh(
      new THREE.TorusGeometry(ringR, 0.018, 12, 48),
      new THREE.MeshStandardMaterial({ color: 0x181818, roughness: 0.6 })
    )
  );
  ringGroup.position.set(0, 0, backZ + 0.3);
  group.add(ringGroup);

  const redGlow = new THREE.PointLight(0xff2a2a, 1.8, 3.5, 2);
  redGlow.position.set(0, 0, backZ + 0.8);
  group.add(redGlow);

  // --- wide wrap straps + buckles ---
  const strapMat = new THREE.MeshStandardMaterial({
    color: 0x111114,
    roughness: 0.93,
    metalness: 0.0,
    side: THREE.DoubleSide,
  });
  const buckleMat = new THREE.MeshStandardMaterial({ color: 0x0a0a0a, roughness: 0.45, metalness: 0.15 });
  [0.62, -0.62].forEach((y) => {
    const curve = new THREE.CatmullRomCurve3([
      new THREE.Vector3(-1.28, y, 0.15),
      new THREE.Vector3(-1.12, y, -0.5),
      new THREE.Vector3(0, y, -0.82),
      new THREE.Vector3(1.12, y, -0.5),
      new THREE.Vector3(1.28, y, 0.15),
    ]);
    const strap = new THREE.Mesh(ribbonGeometry(curve, 48, 0.26), strapMat);
    strap.castShadow = true;
    group.add(strap);
    // plastic buckle on the left side only
    const buckle = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.4, 0.16), buckleMat);
    buckle.position.set(-1.24, y, 0.08);
    group.add(buckle);
  });

  return { group, bezelMat };
}

function mount(container) {
  if (!CDN_OK) return;
  if (container.dataset.mounted === '1') return;
  container.dataset.mounted = '1';

  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'high-performance' });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;
  const loading = container.querySelector('[data-spin-loading]');
  if (loading) loading.remove();
  container.appendChild(renderer.domElement);
  renderer.domElement.style.width = '100%';
  renderer.domElement.style.height = '100%';
  renderer.domElement.style.display = 'block';
  renderer.domElement.style.cursor = 'grab';

  const scene = new THREE.Scene();
  const pmrem = new THREE.PMREMGenerator(renderer);
  scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;

  const camera = new THREE.PerspectiveCamera(32, 1, 0.1, 100);
  camera.position.set(0, 0.15, 7.6);

  const key = new THREE.DirectionalLight(0xffffff, 2.1);
  key.position.set(4, 6, 6);
  key.castShadow = true;
  key.shadow.mapSize.set(1024, 1024);
  key.shadow.camera.near = 1;
  key.shadow.camera.far = 30;
  key.shadow.camera.left = -5;
  key.shadow.camera.right = 5;
  key.shadow.camera.top = 5;
  key.shadow.camera.bottom = -5;
  key.shadow.bias = -0.0004;
  scene.add(key);
  scene.add(new THREE.HemisphereLight(0xffffff, 0xdfe3e8, 0.65));
  const fill = new THREE.DirectionalLight(0xffffff, 0.55);
  fill.position.set(-5, 2, 3);
  scene.add(fill);

  const ground = new THREE.Mesh(new THREE.PlaneGeometry(30, 30), new THREE.ShadowMaterial({ opacity: 0.16 }));
  ground.rotation.x = -Math.PI / 2;
  ground.position.y = -2.0;
  ground.receiveShadow = true;
  scene.add(ground);

  const current = buildDevice();
  scene.add(current.group);

  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.08;
  controls.enablePan = false;
  controls.minDistance = 6;
  controls.maxDistance = 9.5;
  controls.autoRotate = true;
  controls.autoRotateSpeed = 1.3;
  controls.target.set(0, 0, 0);
  controls.addEventListener('start', () => {
    controls.autoRotate = false;
    renderer.domElement.style.cursor = 'grabbing';
  });
  controls.addEventListener('end', () => {
    renderer.domElement.style.cursor = 'grab';
  });

  function resize() {
    const w = container.clientWidth || 1;
    const h = container.clientHeight || 1;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  }
  new ResizeObserver(resize).observe(container);
  resize();

  function animate() {
    controls.update();
    renderer.render(scene, camera);
    requestAnimationFrame(animate);
  }
  animate();

  container.__setTrim = function (key) {
    const isSilver = key === 'silver';
    current.bezelMat.color.set(isSilver ? 0xd7dade : 0x0b0b0d);
    current.bezelMat.metalness = isSilver ? 0.95 : 0.1;
    current.bezelMat.roughness = isSilver ? 0.25 : 0.22;
    current.bezelMat.needsUpdate = true;
  };
}

function getVariantKey() {
  const checked = document.querySelector('variant-selects input[type="radio"]:checked, variant-selects select');
  const val = ((checked && (checked.value || checked.getAttribute('value'))) || '').toLowerCase();
  return val.indexOf('silver') !== -1 ? 'silver' : 'black';
}

function init() {
  document.querySelectorAll('[data-soovana-3d]').forEach((container) => {
    if (container.dataset.ready === '1') return;
    const start = () => {
      if (container.offsetParent === null && container.clientWidth === 0) return false;
      container.dataset.ready = '1';
      mount(container);
      if (container.__setTrim) container.__setTrim(getVariantKey());
      return true;
    };
    if (!start()) {
      const obs = new MutationObserver(() => {
        if (start()) obs.disconnect();
      });
      obs.observe(container.closest('[data-soovana-spin]') || document.body, {
        attributes: true,
        subtree: true,
        attributeFilter: ['hidden', 'style', 'class'],
      });
    }
  });

  document.querySelectorAll('variant-selects').forEach((picker) => {
    picker.addEventListener('change', () => {
      const key = getVariantKey();
      document.querySelectorAll('[data-soovana-3d]').forEach((c) => {
        if (c.__setTrim) c.__setTrim(key);
      });
    });
  });
}

document.addEventListener('DOMContentLoaded', init);
document.addEventListener('shopify:section:load', init);
if (document.readyState !== 'loading') init();
